import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isHomepage = location.pathname === '/';
  const isBlog = location.pathname === '/blog';
  const isDocs = location.pathname === '/documentation';

  const handleSectionClick = (section) => {
    if (!isHomepage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className={`sticky top-0 z-50 w-full border-b border-solid relative ${
      isHomepage 
        ? 'border-[#f1f0f4] dark:border-white/10' 
        : 'border-[#f0f2f4] dark:border-[#2a2d35]'
    } bg-white dark:bg-background-dark px-6 md:px-20 lg:px-40 py-3`}>
      <div className={`flex items-center justify-between ${
        isHomepage ? 'max-w-[1200px] mx-auto' : 'max-w-[1200px] mx-auto'
      } whitespace-nowrap`}>
        <div className="flex items-center gap-4 md:gap-8 flex-1 justify-start md:justify-start">
          <div className="flex items-center gap-2 md:gap-4 text-primary">
            <div className="size-8 flex items-center justify-center bg-primary rounded-lg text-white">
              <span className="material-symbols-outlined text-xl">bolt</span>
            </div>
            <h2 className={`${
              isHomepage 
                ? 'text-[#121118]' 
                : 'text-[#111318]'
            } dark:text-white text-xl font-bold leading-tight ${
              isHomepage 
                ? 'tracking-tight' 
                : 'tracking-[-0.015em]'
            }`}>
              Formly AI
            </h2>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-base font-medium ${
                isHomepage
                  ? 'text-primary font-bold'
                  : 'hover:text-primary transition-colors'
              }`}
            >
              الرئيسية
            </Link>
            <button 
              onClick={() => handleSectionClick('features')} 
              className="text-base font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer"
            >
              المميزات
            </button>
            <button 
              onClick={() => handleSectionClick('how-it-works')} 
              className="text-base font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer"
            >
              كيف يعمل
            </button>
            <button 
              onClick={() => handleSectionClick('faq')} 
              className="text-base font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer"
            >
              الأسئلة الشائعة
            </button>
            <Link to="/blog" className={`text-base font-medium ${
              isBlog 
                ? 'text-primary font-bold' 
                : 'hover:text-primary transition-colors'
            }`}>
              المدونة
            </Link>
            <Link to="/documentation" className={`text-base font-medium ${
              isDocs 
                ? 'text-primary font-semibold border-b-2 border-primary' 
                : 'hover:text-primary transition-colors'
            }`}>
              التوثيق
            </Link>
          </nav>
        </div>

        <div className="flex flex-1 justify-end gap-4 lg:gap-8 items-center">
          {/* Mobile Menu Button - Far Right */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="md:hidden flex items-center justify-center size-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-manipulation select-none"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className="material-symbols-outlined text-2xl">
              {isMenuOpen ? 'close' : 'menu'}
            </span>
          </button>
          
          <Link 
            to="/install" 
            className="hidden md:flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold leading-normal tracking-[0.015em] hover:opacity-90 transition-opacity shadow-sm whitespace-nowrap min-w-[120px] px-5"
          >
            <span>تثبيت الإضافة</span>
          </Link>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        aria-hidden={!isMenuOpen}
        className={`md:hidden absolute top-full inset-x-0 border-t border-[#f1f0f4] dark:border-white/10 bg-white dark:bg-background-dark shadow-lg ${
          isMenuOpen ? 'block' : 'hidden'
        }`}
      >
        <nav className="flex flex-col px-6 py-4 gap-4 max-w-[1200px] mx-auto">
          <Link
            to="/"
            onClick={() => setIsMenuOpen(false)}
            className={`text-sm font-medium py-2 ${
              isHomepage
                ? 'text-primary font-bold'
                : 'hover:text-primary transition-colors'
            }`}
          >
            الرئيسية
          </Link>
          <button
            onClick={() => {
              handleSectionClick('features');
              setIsMenuOpen(false);
            }}
            className="text-right text-sm font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer py-2"
          >
            المميزات
          </button>
          <button
            onClick={() => {
              handleSectionClick('how-it-works');
              setIsMenuOpen(false);
            }}
            className="text-right text-sm font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer py-2"
          >
            كيف يعمل
          </button>
          <button
            onClick={() => {
              handleSectionClick('faq');
              setIsMenuOpen(false);
            }}
            className="text-right text-sm font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer py-2"
          >
            الأسئلة الشائعة
          </button>
          <Link
            to="/blog"
            onClick={() => setIsMenuOpen(false)}
            className={`text-sm font-medium py-2 ${
              isBlog
                ? 'text-primary font-bold'
                : 'hover:text-primary transition-colors'
            }`}
          >
            المدونة
          </Link>
          <Link
            to="/documentation"
            onClick={() => setIsMenuOpen(false)}
            className={`text-sm font-medium py-2 ${
              isDocs
                ? 'text-primary font-semibold'
                : 'hover:text-primary transition-colors'
            }`}
          >
            التوثيق
          </Link>
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 mt-2">
            <Link
              to="/install"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:opacity-90 transition-opacity shadow-sm whitespace-nowrap"
            >
              تثبيت الإضافة
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;

